/**
 * FormulaPalette Component
 *
 * A clickable palette of pre-defined mathematical formulas in LaTeX.
 * When clicked, formulas are inserted into the chat input.
 *
 * Props:
 * - formulas: Array of { label: string, latex: string, description?: string }
 * - onInsert: Callback function called with the LaTeX string when a formula is clicked
 * - disabled: Whether the palette is disabled
 */
import React, { useState } from "react";
import "katex/dist/katex.min.css";
import { InlineMath } from "react-katex";

export function FormulaPalette({ formulas = [], onInsert, disabled = false }) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!formulas || formulas.length === 0) {
    return null;
  }

  return (
    <div className="formula-palette">
      {/* Toggle button */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-800 transition-colors py-1 px-2 rounded hover:bg-indigo-50"
        disabled={disabled}
      >
        <span className="text-lg">∑</span>
        <span>{isExpanded ? "Hide formulas" : "Insert formula"}</span>
        <svg
          className={`w-4 h-4 transition-transform ${isExpanded ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Formula grid */}
      {isExpanded && (
        <div className="mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
          <p className="text-xs text-gray-500 mb-2">Click a formula to insert it into your message:</p>
          <div className="flex flex-wrap gap-2">
            {formulas.map((formula, index) => (
              <FormulaButton
                key={index}
                formula={formula}
                onClick={() => {
                  onInsert(`$${formula.latex}$`);
                  setIsExpanded(false);
                }}
                disabled={disabled}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * FormulaButton Component
 * Individual clickable formula button with preview
 */
function FormulaButton({ formula, onClick, disabled }) {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={onClick}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        disabled={disabled}
        className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg hover:border-indigo-400 hover:bg-indigo-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <span className="text-xs font-medium text-gray-600">{formula.label}</span>
        <span className="text-indigo-700">
          <InlineMath math={formula.latex} />
        </span>
      </button>

      {/* Tooltip with description */}
      {showTooltip && formula.description && (
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap z-10">
          {formula.description}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
        </div>
      )}
    </div>
  );
}
